import {Injectable} from '@angular/core';

import {
  Table,
  TableCellBorder,
  TableCellProps,
  TableCellStyle
} from './table-properties';
import {TableDataExportUtilsService} from './table-data-export-utils.service';

@Injectable({
  providedIn: 'root'
})
export class HtmlTableParserService {
  constructor(private readonly utils: TableDataExportUtilsService) {}

  /**
   * Parses a HTML table DOM element so it can be used for further processing.
   * @param {HTMLTableElement} table A HTML table element from the DOM.
   * @returns {TableCellProps} A matrix (2D Array) of table cell properties
   */
  public parse(table: HTMLTableElement): Table {
    const resultMatrix: Table = [];

    const rows = table.getElementsByTagName('tr');
    for (const row of rows) {
      const tableRowArray: TableCellProps[] = [];
      for (const cell of row.children as HTMLCollectionOf<HTMLTableCellElement>) {
        this.parseTableCell(cell, tableRowArray);
        this.insertColspanCells(cell, tableRowArray);
      }
      resultMatrix.push(tableRowArray);
    }
    this.insertRowspanCells(resultMatrix);

    return resultMatrix;
  }

  /**
   * Parses an HTML elements table decendants.
   * @param rootElement The parent element of the tables to export. If omitted, defaults to `document.body`, so all tables currently present in the DOM are parsed.
   */
  public parseAllTables(
    rootElement: HTMLElement = document.body,
    filterById: string = null
  ): Table[] {
    const tableElements = this.lookupTableElements(rootElement) ?? [];

    const tables: Table[] = [];
    for (const tableElement of tableElements) {
      if (filterById === null || tableElement.id === filterById) {
        const result = this.parse(tableElement);
        tables.push(result);
      }
    }

    return tables;
  }

  private lookupTableElements(rootElement: HTMLElement) {
    const tableElements = rootElement.getElementsByTagName('table');

    if (tableElements.length <= 0) {
      // eslint-disable-next-line no-console
      console.debug('No tables found. Looking in shadow root.');
      const shadowDomTableElements =
        rootElement.shadowRoot?.querySelectorAll('table');

      if (shadowDomTableElements?.length <= 0) {
        throw Error('No table elements found');
      }
      return shadowDomTableElements;
    }

    return tableElements;
  }

  private parseTableCell(
    cell: HTMLTableCellElement,
    tableRowArray: TableCellProps[]
  ) {
    const cellTextContent = cell.textContent.trim();
    const cellNumberValue = (cellTextContent as never) * 1;
    let val: string | number = null;
    if (!isNaN(cellNumberValue) && cellTextContent.length > 0) {
      val = cellNumberValue;
    } else {
      val = cellTextContent.replace(/[\u00AD]/g, ''); // remove $shy; (also called soft hyphen)
    }

    const propsToAdd = {
      value: val,
      colspan: cell.colSpan,
      rowspan: cell.rowSpan,
      styles: []
    } as TableCellProps;

    for (const value of Object.values(TableCellStyle)) {
      if (cell.classList.contains(value)) {
        propsToAdd.styles.push(value);
      }
    }
    for (const value of Object.values(TableCellBorder)) {
      if (cell.classList.contains(value)) {
        propsToAdd.border = value;
      }
    }

    tableRowArray.push(propsToAdd);
  }

  private insertColspanCells(
    cell: HTMLTableCellElement,
    tableRowArray: TableCellProps[]
  ) {
    for (let i = 1; i < cell.colSpan; i++) {
      tableRowArray.push({
        value: ''
      });
    }
  }

  private insertRowspanCells(resultMatrix: TableCellProps[][]) {
    for (const cell of this.utils.findRowSpanningCells(resultMatrix)) {
      for (let rowspanIndex = 1; rowspanIndex < cell.rowspan; rowspanIndex++) {
        for (
          let colspanIndex = 0;
          colspanIndex < cell.colspan;
          colspanIndex++
        ) {
          this.insertEmptyCellInMatrix(
            resultMatrix,
            cell.rowIndex + rowspanIndex,
            cell.colIndex + colspanIndex
          );
        }
      }
    }
  }

  private insertEmptyCellInMatrix(
    resultMatrix: TableCellProps[][],
    rowIndex: number,
    colIndex: number
  ) {
    resultMatrix[rowIndex].splice(colIndex, 0, {
      value: ''
    });
  }
}
